import { showToast } from '../../../engine/toast';
import { adminGenerateInvoice, adminGetUser, adminListPlayers, listItems, type AdminUser } from '../admin-api';
import { fetchStoreCatalog, quoteDeltaVForCents, type StoreCatalog } from '../../shop/shop-api';
import { escapeHtml, INPUT_CLS, BTN_CLS } from '../admin-ui';
import type { ModalHandle } from '../modal';
import type { PaneCtx } from '../panes/types';
import { errorText } from './fields';

/** The rates the business actually issues at. */
const VAT_RATES = [0, 20, 22] as const;
const SEARCH_DEBOUNCE_MS = 300;

interface Draft {
  user: AdminUser | null;
  netCents: number;
  vatRate: number;
}

function parseCents(raw: string): number | null {
  const n = Number(raw.replace(',', '.').trim());
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

const eur = (cents: number): string => (cents / 100).toFixed(2);

function bodyHtml(): string {
  return `
    <form id="gen-invoice" data-form="gen-invoice" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <span class="text-[10px] text-text-dim">Player</span>
        <div data-field="user" class="text-sm text-text">Nobody selected</div>
        <input name="userQuery" type="search" autocomplete="off"
          placeholder="Search by name, email or id" class="${INPUT_CLS}" />
        <ul data-search-results class="flex flex-col"></ul>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">Amount excl. VAT</span>
          <input name="net" type="number" min="0" step="0.01" value="0.00" inputmode="decimal" class="${INPUT_CLS}" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">Amount incl. VAT</span>
          <input name="gross" type="number" min="0" step="0.01" value="0.00" inputmode="decimal" class="${INPUT_CLS}" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">VAT rate</span>
          <select name="vatRate" class="${INPUT_CLS}">
            ${VAT_RATES.map((r) => `<option value="${r}">${r}%</option>`).join('')}
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[10px] text-text-dim">Status</span>
          <select name="status" class="${INPUT_CLS}">
            <option value="PAID">Paid</option>
            <option value="PENDING">Not paid</option>
          </select>
        </label>
      </div>

      <!-- The totals are the point of this dialog, so they are set at a size
           you can check at a glance rather than squinting at 12px. -->
      <div class="glass-panel px-4 py-3 flex flex-col gap-2">
        <div class="grid grid-cols-2 gap-x-4 items-baseline">
          <span class="text-[10px] text-text-dim uppercase tracking-wide">Net</span>
          <span data-field="net" class="text-lg text-text tabular-nums text-right">0.00 EUR</span>
        </div>
        <div class="grid grid-cols-2 gap-x-4 items-baseline">
          <span class="text-[10px] text-text-dim uppercase tracking-wide">VAT</span>
          <span data-field="vat" class="text-lg text-text tabular-nums text-right">0.00 EUR</span>
        </div>
        <div class="grid grid-cols-2 gap-x-4 items-baseline pt-2 border-t border-border">
          <span class="text-[10px] text-gold uppercase tracking-wide">Total</span>
          <span data-field="gross" class="text-3xl text-gold tabular-nums text-right leading-none">0.00 EUR</span>
        </div>
        <div class="grid grid-cols-2 gap-x-4 items-baseline">
          <span class="text-[10px] text-text-dim uppercase tracking-wide">Delta-V equivalent</span>
          <span data-field="deltav" class="text-lg text-purple-light tabular-nums text-right">—</span>
        </div>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-[10px] text-text-dim">Description</span>
        <input name="description" type="text" required placeholder="What this invoice is for" class="${INPUT_CLS}" />
      </label>

      <div class="grid grid-cols-2 gap-3">
        ${['paymentMethod', 'transactionId']
          .map(
            (n) => `
          <label class="flex flex-col gap-1">
            <span class="text-[10px] text-text-dim">${n === 'paymentMethod' ? 'Payment method' : 'Transaction id'}</span>
            <input name="${n}" type="text" autocomplete="off"
              placeholder="${n === 'transactionId' ? 'Optional' : 'Card, bank transfer…'}" class="${INPUT_CLS}" />
          </label>`,
          )
          .join('')}
      </div>

      <!-- Prefilled from the player's own Settings → Billing and Account once
           one is picked. Editable, because an invoice sometimes has to be
           addressed to a company rather than the account holder. The posted
           ADDRESS is not taken from here at all: createInvoice snapshots it
           server-side from the user, so the document records who was billed
           at the time rather than whatever was typed. -->
      <div class="flex flex-col gap-3">
        <h3 class="text-[10px] text-text-dim uppercase tracking-wide">Recipient</h3>
        <div class="grid grid-cols-2 gap-3">
          <label class="flex flex-col gap-1">
            <span class="text-[10px] text-text-dim">First name</span>
            <input name="recipientName" type="text" autocomplete="off" class="${INPUT_CLS}" />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[10px] text-text-dim">Surname</span>
            <input name="recipientSurname" type="text" autocomplete="off" class="${INPUT_CLS}" />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[10px] text-text-dim">Company</span>
            <input name="recipientCompany" type="text" autocomplete="off" class="${INPUT_CLS}" />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-[10px] text-text-dim">Registration / VAT code</span>
            <input name="recipientCode" type="text" autocomplete="off" class="${INPUT_CLS}" />
          </label>
        </div>
        <p class="text-[10px] text-text-dim leading-relaxed" data-field="billing">
          Pick a player to load their billing address.
        </p>
      </div>

      <div class="flex justify-end gap-2">
        <button type="button" data-action="close" class="min-h-11 px-4 text-sm text-text-dim hover:text-text touch-manipulation">Cancel</button>
        <button type="submit" class="${BTN_CLS}">Generate</button>
      </div>
    </form>`;
}

function resultsHtml(users: AdminUser[]): string {
  if (users.length === 0) return '';
  return users
    .map(
      (u) => `
      <li>
        <button type="button" data-action="pick-user" data-id="${u.id}"
          class="w-full text-left min-h-11 px-2 py-1 border-b border-border text-xs hover:bg-surface-2 touch-manipulation">
          <span class="text-text">${escapeHtml(u.username ?? '—')}</span>
          <span class="text-text-dim"> · ${escapeHtml(u.email)}</span>
        </button>
      </li>`,
    )
    .join('');
}

/**
 * Create an invoice.
 *
 * THE RULE IN THIS FILE: the form is rendered once and never re-rendered.
 * Every derived value is patched into its own `[data-field]` node on `input`.
 * Replacing `innerHTML` while someone is typing would drop focus and caret —
 * the same reason `screens/shop/shop-screen.ts` patches its checkout
 * read-outs instead of repainting.
 *
 * The server is the authority on the arithmetic: it recomputes
 * `vatCents = round(amountCents * vatRate / 100)` from the NET figure we
 * submit, so the numbers shown here must use that identical formula or the
 * PDF would disagree with the dialog.
 */
export function openGenerateInvoice(ctx: PaneCtx): void {
  const draft: Draft = { user: null, netCents: 0, vatRate: VAT_RATES[0] };
  let catalog: StoreCatalog | null = null;
  let searchTimer: ReturnType<typeof setTimeout> | null = null;
  // Monotonic counter, not an AbortController: a slower earlier response must
  // not overwrite a newer one, and nothing else in this codebase aborts.
  let searchSeq = 0;

  function paintTotals(h: ModalHandle, source: 'net' | 'gross'): void {
    const netEl = h.el.querySelector('[name="net"]') as HTMLInputElement;
    const grossEl = h.el.querySelector('[name="gross"]') as HTMLInputElement;

    if (source === 'gross') {
      const grossCents = parseCents(grossEl.value) ?? 0;
      // Back-solve the net from the gross so an operator can work from the
      // figure the customer actually paid.
      draft.netCents = Math.round((grossCents * 100) / (100 + draft.vatRate));
    } else {
      draft.netCents = parseCents(netEl.value) ?? 0;
    }

    const vatCents = Math.round((draft.netCents * draft.vatRate) / 100);
    const grossCents = draft.netCents + vatCents;

    h.setField('net', `${eur(draft.netCents)} EUR`);
    h.setField('vat', `${eur(vatCents)} EUR`);
    h.setField('gross', `${eur(grossCents)} EUR`);
    h.setField(
      'deltav',
      catalog
        ? `${quoteDeltaVForCents(grossCents, catalog.deltaVPacks, catalog.deltaVCustomMinCents, catalog.deltaVCustomMaxCents).toLocaleString('en-US')} Δv`
        : '—',
    );

    // Mirror into the other input, but never into the one being typed in —
    // writing `.value` of a focused field moves the caret to the end.
    const mirror = source === 'gross' ? netEl : grossEl;
    if (document.activeElement !== mirror) {
      mirror.value = eur(source === 'gross' ? draft.netCents : grossCents);
    }
  }

  /**
   * Fills the recipient fields from the player's own Settings -> Billing and
   * Account, and shows the address that will be snapshotted.
   *
   * Only ever writes into a field the operator has not already typed in, so
   * picking a different player cannot silently discard an override.
   */
  async function prefillFromAccount(h: ModalHandle, userId: string): Promise<void> {
    let detail;
    try {
      detail = await adminGetUser(userId);
    } catch {
      h.setField('billing', 'Could not load this player’s billing details.');
      return;
    }
    const d = detail as unknown as Record<string, string | null>;

    const put = (name: string, value: string | null | undefined) => {
      const el = h.el.querySelector(`[name="${name}"]`) as HTMLInputElement | null;
      if (el && !el.value.trim() && value) el.value = value;
    };
    put('recipientName', d.firstName);
    put('recipientSurname', d.lastName);

    const address = [d.billingLine1, d.billingLine2, d.billingCity, d.billingZip, d.billingState, d.billingCountry]
      .filter((p) => p && String(p).trim())
      .join(', ');
    h.setField(
      'billing',
      address
        ? `Billing address on file: ${address}`
        : 'This player has no billing address saved — the invoice will show only their name and email.',
    );
  }

  function setResults(h: ModalHandle, users: AdminUser[]): void {
    const list = h.el.querySelector('[data-search-results]');
    if (list) list.innerHTML = resultsHtml(users);
  }

  function scheduleSearch(term: string, h: ModalHandle): void {
    if (searchTimer) clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      const q = term.trim();
      if (q.length < 2) {
        setResults(h, []);
        return;
      }
      const seq = ++searchSeq;
      void (async () => {
        try {
          // The GAME list route: it matches an exact id as well as name and
          // email, which is what lets an operator paste a player id here.
          const res = await adminListPlayers({ search: q, pageSize: 8 });
          if (seq !== searchSeq) return; // a newer keystroke already answered
          setResults(h, listItems(res).slice(0, 8));
        } catch {
          if (seq === searchSeq) setResults(h, []);
        }
      })();
    }, SEARCH_DEBOUNCE_MS);
  }

  const handle = ctx.openModal({
    title: 'Generate invoice',
    size: 'lg',
    body: bodyHtml(),

    onAction(action, el, h) {
      if (action !== 'pick-user') return;
      const id = el.dataset.id ?? '';
      void (async () => {
        const res = await adminListPlayers({ search: id, pageSize: 1 });
        const user = listItems(res).find((u) => u.id === id);
        if (!user) return;
        draft.user = user;
        h.setField('user', `${user.username ?? '—'} · ${user.email}`);
        setResults(h, []);
        const input = h.el.querySelector('[name="userQuery"]') as HTMLInputElement;
        input.value = '';
        await prefillFromAccount(h, user.id);
      })();
    },

    onInput(target, h) {
      const name = (target as HTMLInputElement).name;
      if (name === 'net') paintTotals(h, 'net');
      else if (name === 'gross') paintTotals(h, 'gross');
      else if (name === 'vatRate') {
        draft.vatRate = Number((target as HTMLSelectElement).value);
        paintTotals(h, 'net');
      } else if (name === 'userQuery') {
        scheduleSearch((target as HTMLInputElement).value, h);
      }
    },

    onSubmit(kind, form, h) {
      if (kind !== 'gen-invoice') return;
      if (!draft.user) {
        h.setError('Pick a player first.');
        return;
      }
      const data = new FormData(form);
      const description = String(data.get('description') ?? '').trim();
      if (!description) {
        h.setError('A description is required.');
        return;
      }
      if (draft.netCents < 1) {
        h.setError('The amount must be greater than zero.');
        return;
      }

      h.setBusy(true);
      void (async () => {
        try {
          // NET cents and a percent — the server derives VAT and the total
          // itself. Submitting the gross would double-count the VAT.
          await adminGenerateInvoice({
            userId: draft.user!.id,
            type: 'IN',
            amountCents: draft.netCents,
            vatRate: draft.vatRate,
            currency: 'EUR',
            description,
            status: String(data.get('status') ?? 'PAID') as 'PAID' | 'PENDING',
            paymentMethod: String(data.get('paymentMethod') ?? '').trim() || undefined,
            transactionId: String(data.get('transactionId') ?? '').trim() || undefined,
            recipientName: String(data.get('recipientName') ?? '').trim() || undefined,
            recipientSurname: String(data.get('recipientSurname') ?? '').trim() || undefined,
            recipientCompany: String(data.get('recipientCompany') ?? '').trim() || undefined,
            recipientCode: String(data.get('recipientCode') ?? '').trim() || undefined,
          });
          showToast('Invoice generated.', 'success');
          h.close();
          await ctx.reload('invoices');
        } catch (err) {
          h.setError(errorText(err, 'Could not generate that invoice.'));
          h.setBusy(false);
        }
      })();
    },

    onClose() {
      if (searchTimer) clearTimeout(searchTimer);
    },
  });

  // The Delta-V equivalent needs the live pack ladder; the rest of the dialog
  // works without it and the figure stays '—' if it never arrives.
  void (async () => {
    try {
      catalog = await fetchStoreCatalog();
      if (!ctx.disposed()) paintTotals(handle, 'net');
    } catch {
      /* display-only, never submitted */
    }
  })();
}
